import { App, Card, Col, Row, Statistic, Typography, theme } from 'antd';
import ReferralStatusTable from './components/ReferralStatusTable';
import ReferralForm from './components/ReferralForm';
import { useGetReferralStatsQuery, useGetReferralsQuery, useSendReferralMutation } from './referralsApiSlice';
import InformationBlock from './components/InformationBlock';
import { useEffect, useMemo, useState } from 'react';
import { useEvent } from '../../events/hooks/useEvent';
import { usePrerenderUser } from '../../../hooks/usePrerenderUser';

const PAGE_SIZE = 10;

const mapToApiPayload = (params) => {
  return {
    page: params.pagination.current,
    pageSize: params.pagination.pageSize,
    sort: params.sorter?.field,
    sortDirection: params.sorter?.order === 'ascend' ? 'ASC' : 'DESC',
  };
};

const convertToDollars = (totalInCents) => {
  const totalInDollars = (totalInCents / 100).toFixed(2);
  return totalInDollars;
};

const NO_TOTAL = '-';

const ReferralOverview = () => {
  const { message } = App.useApp();
  const initialParams = {
    pagination: {
      current: 1,
      pageSize: PAGE_SIZE,
    },
    sorter: {
      field: 'referredOn',
      order: 'descend',
    },
  };
  const [tableParams, setTableParams] = useState(initialParams);
  const { data: totalData, isFetching } = useGetReferralStatsQuery();
  const {
    data: referrals,
    error: referralsError,
    isFetching: isFetchingReferrals,
  } = useGetReferralsQuery(mapToApiPayload(tableParams), { refetchOnMountOrArgChange: true });
  const [sendReferral, { isLoading }] = useSendReferralMutation();
  const { track } = useEvent();
  const user = usePrerenderUser();
  const { token } = theme.useToken();
  const totalReferrals = referrals?.totalCount || 0;
  // rest-api sums an integer column, so Postgres sends a bigint and node-pg passes it on as a string.
  const totalCreditsEarned = Number(totalData?.totalCreditsEarned);
  const hasTotalCreditsEarned = Number.isFinite(totalCreditsEarned);

  const tableData = useMemo(() => {
    return (
      referrals?.data?.map((item) => {
        return {
          key: item.id,
          name: item.name,
          email: item.email,
          status: item.status,
          referredOn: item.referredOn,
        };
      }) || []
    );
  }, [referrals]);

  useEffect(() => {
    if (referralsError) {
      message.error('Failed to fetch referrals');
    }
  }, [referralsError?.data?.error]);

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      sorter,
    });
  };

  const onSendReferral = (referralData) => {
    sendReferral(referralData)
      .unwrap()
      .then(() => {
        message.success('Referral sent successfully');
        track('Referral page: Send message clicked', {
          subscription_plan: user.planName,
          invited_email: referralData.email,
          successfull: true,
        });
      })
      .catch((error) => {
        message.error(error.data?.message || 'Failed to send referral');
        track('Referral page: Send message clicked', {
          subscription_plan: user.planName,
          invited_email: referralData.email,
          successfull: false,
        });
      });
  };

  return (
    <div className="referral-overview">
      <Row gutter={[24, 24]}>
        <Col xs={24} xl={17}>
          <Card
            title={
              <>
                <Typography.Title level={4} style={{ marginTop: token.paddingSM }}>
                  Refer & Earn
                </Typography.Title>
                <Typography.Paragraph style={{ fontWeight: 'normal', whiteSpace: 'normal' }}>
                  Enjoy $45 for every successful referral.
                </Typography.Paragraph>
              </>
            }
          >
            <InformationBlock />
          </Card>
        </Col>
        <Col xs={8} xl={7}>
          <Card title="Get $45 Off" loading={isLoading}>
            <ReferralForm onFinish={onSendReferral} isLoading={isLoading} />
          </Card>
        </Col>
        <Col xs={24} xl={17}>
          <Card title="Referral Status">
            <ReferralStatusTable
              tableData={tableData}
              isFetchingReferrals={isFetchingReferrals}
              handleTableChange={handleTableChange}
              tableParams={tableParams}
              totalReferrals={totalReferrals}
            />
          </Card>
        </Col>
        <Col xs={8} xl={7}>
          <Card title="Total Credits Earned">
            <div>
              <Statistic
                value={hasTotalCreditsEarned ? convertToDollars(totalCreditsEarned) : NO_TOTAL}
                prefix={hasTotalCreditsEarned ? '$' : undefined}
                loading={isFetching}
                style={{ display: 'flex', justifyContent: 'center' }}
              />
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ReferralOverview;
